import * as React from "react";
import classnames from "classnames";
import { AnimatePresence, motion } from "motion/react";
import { Heading, Hr } from "@grandvisionhq/elements";
import { Icon } from "@grandvisionhq/icons";
import { useIntl } from "@grandvisionhq/state";
import Backdrop from "../../Backdrop/Backdrop/Backdrop.js";
import IconButton from "../../IconButton/IconButton/IconButton.js";
import { Portal } from "../../Portal/Portal/Portal.js";
import { unlockScroll, lockScroll } from "../../Util/Util/scroll-lock.js";
/* empty css           */
import { useStackedHandleOnEscape } from "../../EscapeHandlerStack/hooks/EscapeHandlerStack/hooks/use-stacked-handle-on-escape.js";
import { FocusTrap } from "../../FocusTrap/FocusTrap/FocusTrap.js";
const Modal = ({
  activeTrap,
  "aria-label": ariaLabel,
  isOpen,
  isFullScreen = false,
  preventVerticalScroll,
  className,
  title,
  titleVariant,
  footer,
  showIcon,
  secondaryIconOptions,
  secondaryIconsSlot,
  iconClassName,
  onModalClose,
  children,
  isOutsideClickEnabled = true,
  ...rest
}) => {
  const { getLabel } = useIntl();
  const isOpenRef = React.useRef(isOpen);
  const hideModal = React.useCallback(() => {
    if (isOpen) {
      unlockScroll();
      onModalClose?.();
    }
  }, [isOpen, onModalClose]);
  useStackedHandleOnEscape({ isOpen, handler: hideModal });
  React.useEffect(() => {
    isOpenRef.current = isOpenRef.current || isOpen;
    if (isOpen) {
      lockScroll();
      return unlockScroll;
    } else if (isOpenRef.current) {
      unlockScroll();
    }
  }, [isOpen]);
  const transition = {
    stiffness: 500
  };
  const variants = {
    exit: {
      opacity: 0,
      y: 50
    },
    idle: {
      opacity: 1,
      y: 0
    }
  };
  return /* @__PURE__ */ React.createElement(Portal, null, /* @__PURE__ */ React.createElement(AnimatePresence, null, isOpen && /* @__PURE__ */ React.createElement(FocusTrap, { active: activeTrap, focusTrapOptions: { clickOutsideDeactivates: true } }, /* @__PURE__ */ React.createElement("div", { "data-t": rest["data-t"], className: classnames("modal", className) }, /* @__PURE__ */ React.createElement(
    Backdrop,
    {
      className: "modal__backdrop",
      onClick: isOutsideClickEnabled ? hideModal : void 0,
      isVisible: isOpen,
      animate: false
    }
  ), /* @__PURE__ */ React.createElement(
    motion.div,
    {
      transition,
      variants,
      initial: "exit",
      animate: "idle",
      exit: "exit",
      className: classnames("modal__body", {
        "modal__body--flex": !!title || !!footer,
        "modal__body--fullscreen": isFullScreen,
        "modal__body--vertical-scroll-lock": preventVerticalScroll,
        [`${className}__body`]: !!className
      }),
      "data-t": "modal-body",
      "aria-label": ariaLabel || title,
      role: "dialog",
      "aria-modal": "true"
    },
    secondaryIconOptions && /* @__PURE__ */ React.createElement(
      IconButton,
      {
        "aria-label": secondaryIconOptions.label || secondaryIconOptions["aria-label"],
        className: classnames("modal__secondary-icon", {
          "modal__secondary-icon--with-label": !!secondaryIconOptions.label
        }),
        onClick: secondaryIconOptions.onClick
      },
      /* @__PURE__ */ React.createElement(Icon, { name: secondaryIconOptions.name }),
      !!secondaryIconOptions.label && /* @__PURE__ */ React.createElement("span", { className: "modal__secondary-icon-label" }, secondaryIconOptions.label)
    ),
    secondaryIconsSlot,
    showIcon && /* @__PURE__ */ React.createElement(
      IconButton,
      {
        className: classnames("modal__close", iconClassName),
        "aria-label": getLabel("common.actions.close"),
        onClick: hideModal,
        "data-t": "modal-close"
      },
      /* @__PURE__ */ React.createElement(Icon, { name: "close" })
    ),
    title && /* @__PURE__ */ React.createElement(
      "header",
      {
        className: classnames("modal__header", {
          [`${className}__header`]: !!className
        })
      },
      /* @__PURE__ */ React.createElement(
        Heading,
        {
          variant: titleVariant ?? "smallest",
          component: "h2",
          className: "modal__title"
        },
        title
      ),
      /* @__PURE__ */ React.createElement(Hr, null)
    ),
    /* @__PURE__ */ React.createElement(
      "div",
      {
        className: classnames("modal__content", {
          [`${className}__content`]: !!className,
          "modal__content--with-footer": !!footer
        })
      },
      children
    ),
    footer && /* @__PURE__ */ React.createElement(
      "footer",
      {
        className: classnames("modal__footer", {
          [`${className}__footer`]: !!className
        })
      },
      /* @__PURE__ */ React.createElement(Hr, null),
      footer
    )
  )))));
};
export {
  Modal as default
};
//# sourceMappingURL=Modal.js.map
